import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import type { Trace } from '@ipfabric/utils/settings/types/types';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

const PROTOCOL_OPTIONS = [
  { label: 'ICMP', value: 'icmp' },
  { label: 'UDP', value: 'udp' },
  { label: 'TCP', value: 'tcp' },
] as const;

export const traceProtocol: FormSnippet<
  DiscoverySettingsObject,
  { traceProtocol: Trace['protocol']; tracePort: string },
  { traceProtocol: Trace['protocol']; tracePort: string },
  { protocol: Trace['protocol'] }
> = {
  render: ({ form, hook }) => (
    <div className='flex flex-wrap'>
      <div className='flex'>
        <div className='w-60'>
          <FormSelect.Mandatory
            control={form.control}
            name='traceProtocol'
            label='Protocol'
            options={PROTOCOL_OPTIONS}
          />
        </div>
        {hook.protocol !== 'icmp' && (
          <div className='w-60 ml-2'>
            <FormTextField
              control={form.control}
              name='tracePort'
              label='Port'
              type='number'
              min={1}
              max={65535}
            />
          </div>
        )}
      </div>
    </div>
  ),
  use: ({ form }) => {
    const protocol = useWatch({
      control: form.control,
      name: 'traceProtocol',
    });
    return { protocol };
  },
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.traceProtocol = model.traceroute?.protocol ?? 'icmp';
    const existingPort = model.traceroute?.port;
    fieldValuesDraft.tracePort = String(
      typeof existingPort === 'number' ? existingPort : 33434,
    );
  },
  toModel: (transformedValues, modelDraft) => {
    // The API traceroute type is a discriminated union on `protocol`
    const { traceProtocol: protocol } = transformedValues;
    const scope = modelDraft.traceroute?.scope;
    if (protocol === 'icmp') {
      // icmp variant requires port to be null
      modelDraft.traceroute = { protocol, port: null, scope };
    } else {
      // udp/tcp variant requires a numeric port, preserve existing or default to 33434
      const port = Number.parseInt(transformedValues.tracePort, 10) || 33434;
      modelDraft.traceroute = { protocol, port, scope };
    }
  },
  schema: z.object({
    traceProtocol: z.enum(['icmp', 'udp', 'tcp']),
    tracePort: z.string().refine((val) => {
      const num = Number.parseInt(val, 10);
      return num >= 1 && num <= 65535;
    }, 'Must be a number between 1 and 65535'),
  }),
};
